/**
 * 新闻正文：兼容 Typora / CommonMark / GFM 的 Markdown。
 * 可直接在 Typora 中编辑后粘贴到 Content 字段。
 *
 * 本地图片写法（与封面 Img 字段相同）：
 *   ![说明](imagestore/news/kechuang.png)
 * 外链图片仍写完整 URL 即可。
 */
import { marked } from 'marked';

// webpack 打包 src/assets 下图片，供 Markdown 正文引用
const assetImages = require.context(
  '@/assets',
  true,
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i
);

marked.setOptions({
  gfm: true,
  breaks: false,
  headerIds: false,
  mangle: false,
  pedantic: false,
  smartLists: true
});

/** 将 imagestore/... 等本地路径解析为打包后的 URL */
export function resolveContentImage(src) {
  if (!src) return src;
  const raw = String(src).trim();

  // 外链 / data / 已是打包路径，原样返回
  if (
    /^(https?:)?\/\//i.test(raw) ||
    raw.startsWith('data:') ||
    raw.startsWith('blob:')
  ) {
    return raw;
  }

  // 去掉 ./、/、assets/ 前缀，统一成 assets 内相对路径
  let path = raw
    .replace(/\\/g, '/')
    .replace(/^\.?\/+/, '')
    .replace(/^assets\//i, '');

  const key = `./${path}`;
  if (assetImages.keys().includes(key)) {
    return assetImages(key);
  }

  // 容错：只按文件名匹配（Typora 有时带多余目录）
  const fileName = path.split('/').pop();
  const matched = assetImages.keys().find(k => k.endsWith('/' + fileName) || k === './' + fileName);
  if (matched) {
    return assetImages(matched);
  }

  if (typeof console !== 'undefined') {
    console.warn('[newsContent] 本地图片未找到:', raw, '（请确认文件在 src/assets/ 下）');
  }
  return raw;
}

function rewriteHtmlImages(html) {
  return html.replace(
    /<img([^>]*?)src=["']([^"']+)["']([^>]*)>/gi,
    (match, pre, src, post) => {
      const resolved = resolveContentImage(src);
      // 避免重复转义；保持原有属性
      return `<img${pre}src="${resolved}"${post}>`;
    }
  );
}

/** 列表 / 首页摘要：只保留可读纯文本 */
export function toPlainText(content) {
  if (!content) return '';
  let text = String(content);
  // 若已是 HTML，先去标签
  text = text.replace(/<[^>]+>/g, ' ');
  // 常见 Markdown 标记
  text = text.replace(/```[\s\S]*?```/g, ' ');
  text = text.replace(/`([^`]+)`/g, '$1');
  text = text.replace(/!\[[^\]]*\]\([^)]+\)/g, ' ');
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  text = text.replace(/^#{1,6}\s+/gm, '');
  text = text.replace(/^\s{0,3}>\s?/gm, '');
  text = text.replace(/^\s*[-*+]\s+/gm, '');
  text = text.replace(/^\s*\d+\.\s+/gm, '');
  text = text.replace(/(\*\*|__)(.*?)\1/g, '$2');
  text = text.replace(/(\*|_)(.*?)\1/g, '$2');
  text = text.replace(/~~(.*?)~~/g, '$1');
  text = text.replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, ' ');
  text = text.replace(/\|/g, ' ');
  text = text.replace(/&nbsp;/gi, ' ');
  text = text.replace(/&amp;/gi, '&');
  text = text.replace(/&lt;/gi, '<');
  text = text.replace(/&gt;/gi, '>');
  text = text.replace(/&quot;/gi, '"');
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * 详情页：Markdown → HTML。
 * 纯文本（无 MD 语法）也会按空行分成段落。
 */
export function formatNewsContent(content) {
  if (!content) return '';
  const raw = String(content).trim();
  if (!raw) return '';

  let html = marked.parse(raw);
  if (typeof html !== 'string') html = String(html);

  // 本地图片路径 → webpack 资源 URL
  html = rewriteHtmlImages(html);

  // 首个文字段落作为导语（跳过仅含图片的段落）
  html = html.replace(/<p>(?!<img\b)/i, '<p class="article-lead">');

  // 引用块加样式类（与现有 CSS 对齐）
  html = html.replace(
    /<blockquote>/g,
    '<blockquote class="article-quote">'
  );

  // 水平线后的短段落视作文末署名
  html = html.replace(
    /(<hr\s*\/?>)\s*<p>([\s\S]*?)<\/p>/i,
    (match, hr, inner) => {
      const plain = inner.replace(/<[^>]+>/g, '').trim();
      if (plain.length > 0 && plain.length < 80) {
        return `${hr}<p class="article-end">${inner}</p>`;
      }
      return match;
    }
  );

  return html;
}
